import { useCallback, useEffect, useState } from "react";
import { api } from "./api.js";

export type FetchState<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
};

/** Tiny `useFetch` for REST GETs that don't need TanStack Query. Refetch on
 *  demand, no caching, no retry — replicants handle the live data, this is
 *  for one-shot catalog lookups. */
export function useFetch<T>(path: string | null): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((d) => {
        if (cancelled) return;
        setData(d);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, tick]);

  // Stable identity — consumers can safely list `refetch` in effect deps
  // without triggering an infinite render/fetch loop (setTick is stable).
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { data, loading, error, refetch };
}
